import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function HeroSection() {
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'restaurants' | 'boutiques'>('restaurants');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const path = type === 'restaurants' ? '/restaurants' : '/boutiques';
    navigate(`${path}?q=${encodeURIComponent(query)}`);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/dfd8af9a8d5e352b521f113b0d7899ec.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full pt-20 md:pt-0">
        <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-4 leading-tight tracking-tight drop-shadow-lg">
          Livraison locale,<br />
          <span className="text-teal-300">saveurs authentiques</span>
        </h1>
        <p className="text-xl md:text-2xl text-white mb-4 max-w-3xl mx-auto">
          Votre plateforme locale de livraison
        </p>
        <p className="text-base md:text-lg text-white/90 mb-8 max-w-2xl mx-auto">
          Commandez auprès de vos restaurants et boutiques préférés. Livré par des habitants indépendants qui connaissent votre quartier.
        </p>

        {/* Barre de recherche */}
        <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8 px-2">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center bg-white rounded-2xl sm:rounded-full shadow-2xl overflow-hidden p-2 gap-2">
            {/* Sélecteur de type */}
            <div className="flex bg-gray-100 rounded-xl sm:rounded-full p-1 shrink-0">
              <button
                type="button"
                onClick={() => setType('restaurants')}
                className={`flex-1 sm:flex-none px-3 py-2 rounded-lg sm:rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  type === 'restaurants'
                    ? 'bg-teal-600 text-white shadow'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className="ri-restaurant-2-line mr-1"></i>
                Restaurants
              </button>
              <button
                type="button"
                onClick={() => setType('boutiques')}
                className={`flex-1 sm:flex-none px-3 py-2 rounded-lg sm:rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  type === 'boutiques'
                    ? 'bg-teal-600 text-white shadow'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className="ri-store-2-line mr-1"></i>
                Boutiques
              </button>
            </div>

            {/* Champ texte */}
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={type === 'restaurants' ? 'Pizza, sushi, burger...' : 'Épicerie, fleurs, pharmacie...'}
              className="flex-1 px-4 py-2.5 text-sm text-gray-700 bg-transparent outline-none placeholder-gray-400 border-t sm:border-t-0 border-gray-100"
            />

            {/* Bouton recherche */}
            <button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-xl sm:rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 shrink-0"
            >
              <i className="ri-search-line"></i>
              Rechercher
            </button>
          </div>
        </form>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link 
            to="/commander" 
            className="bg-teal-600 text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            Commander maintenant
          </Link>
          <Link 
            to="/livraison" 
            className="bg-white text-teal-600 px-8 py-4 rounded-full text-base font-semibold hover:bg-gray-50 transition-all shadow-lg whitespace-nowrap cursor-pointer"
          >
            Devenir livreur
          </Link>
          <Link 
            to="/partenaire" 
            className="bg-white/20 backdrop-blur-sm text-white border border-white/50 px-8 py-4 rounded-full text-base font-semibold hover:bg-white/30 transition-all shadow-lg whitespace-nowrap cursor-pointer"
          >
            Devenir partenaire
          </Link>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mx-auto mb-4">
              <i className="ri-restaurant-2-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">50+ Restaurants</h3>
            <p className="text-sm text-white/80">Découvrez la diversité culinaire locale</p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mx-auto mb-4">
              <i className="ri-store-2-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">30+ Boutiques</h3>
            <p className="text-sm text-white/80">Soutenez les commerces locaux</p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-600 rounded-full mx-auto mb-4">
              <i className="ri-e-bike-2-line text-2xl text-white"></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Livraison rapide</h3>
            <p className="text-sm text-white/80">En moyenne 30 minutes</p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <i className="ri-arrow-down-line text-3xl text-white"></i>
      </div>
    </section>
  );
}
